import { AccountingImportDoctolibCsvApiResponseError } from '@omecare/utils';
import { format } from 'date-fns/format';
import { intlFormat } from 'date-fns/intlFormat';
import { parseISO } from 'date-fns/parseISO';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useGetDoctorSubstituteQuery } from '@/hooks/queries/use-get-doctor-substitute';
import { errorTranslationKeyOf } from '@/query-client';
import { doctorFullnameTrad } from '@/utils/doctor';
import { ImportAccountingAgendaSlotListReport } from './import-accounting-agenda-slot-list-report';
import {
  ImportAccountingDoctorAttendingCsvReportModalApi,
  useImportAccountingDoctorAttendingCsvReportModalViewModel,
} from './import-accounting-doctor-attending-csv-report-modal.viewmodel';

const DoctorSubstituteName = ({ id }: { id: string }) => {
  const { t } = useTranslation();
  const { data } = useGetDoctorSubstituteQuery({ id });

  if (!data) {
    return <span>...</span>;
  }

  const { key, params } = doctorFullnameTrad(data);
  return <span>{t(key, params)}</span>;
};

export const ImportAccountingDoctorAttendingCsvReportModal = ({
  report,
  officeId,
  onClose,
}: ImportAccountingDoctorAttendingCsvReportModalApi & {
  officeId: string;
  report: {
    error: AccountingImportDoctolibCsvApiResponseError;
  };
}) => {
  const { t } = useTranslation();
  const viewModel = useImportAccountingDoctorAttendingCsvReportModalViewModel({
    api: {
      onClose,
    },
  });

  const contentByErrorType: ReactNode = (() => {
    if (
      report.error.error === 'accounting_agenda_slot_invalid_information' ||
      report.error.error === 'accounting_agenda_slot_missing_information'
    ) {
      const agendaSlots = [report.error.data.agendaSlot];
      return (
        <ImportAccountingAgendaSlotListReport
          title={errorTranslationKeyOf(report.error.error)}
          agendaSlots={agendaSlots}
          officeId={officeId}
        />
      );
    }

    if (report.error.error === 'accounting_agenda_slot_validate_report_error') {
      return (
        <div className="flex flex-col gap-2">
          {report.error.data.slotsWithInvalidInformation.length > 0 && (
            <ImportAccountingAgendaSlotListReport
              title={errorTranslationKeyOf(
                'accounting_agenda_slot_invalid_information',
              )}
              agendaSlots={report.error.data.slotsWithInvalidInformation}
              officeId={officeId}
            />
          )}

          {report.error.data.slotsWithMissingInformation.length > 0 && (
            <ImportAccountingAgendaSlotListReport
              title={errorTranslationKeyOf(
                'accounting_agenda_slot_missing_information',
              )}
              agendaSlots={report.error.data.slotsWithMissingInformation}
              officeId={officeId}
            />
          )}
        </div>
      );
    }

    if (
      report.error.error ===
        'accounting_agenda_slots_morning_doctolib_data_no_intersection' ||
      report.error.error === 'accounting_agenda_slots_doctolib_data_missing'
    ) {
      return (
        <ImportAccountingAgendaSlotListReport
          title={errorTranslationKeyOf(report.error.error)}
          agendaSlots={report.error.data.agendaSlots}
          officeId={officeId}
        />
      );
    }

    if (report.error.error === 'accounting_period_has_aa_warnings') {
      return (
        <ImportAccountingAgendaSlotListReport
          title={errorTranslationKeyOf(report.error.error)}
          agendaSlots={report.error.data.agendaSlots}
          officeId={officeId}
          context={report.error.data.context}
        />
      );
    }

    if (
      report.error.error ===
      'accounting_missing_site_medical_assistant_fee_missing_information'
    ) {
      const formattedPeriods = report.error.data.missingPeriods.map(
        (period) => {
          const [year, month] = period.split('-');
          return intlFormat(new Date(Number(year), Number(month) - 1, 1), {
            year: 'numeric',
            month: 'long',
          });
        },
      );

      return (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="font-semibold text-md">
              {t(errorTranslationKeyOf(report.error.error), {
                siteName: report.error.data.siteName,
                periods: formattedPeriods.join(', '),
              })}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-3">
            <ul className="list-disc pl-4 text-muted-foreground text-sm">
              {formattedPeriods.map((period) => (
                <li key={period}>{period}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      );
    }

    if (report.error.error === 'accounting_period_already_exists') {
      return (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="font-semibold text-md">
              {t(errorTranslationKeyOf(report.error.error))}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-3">
            {report.error.data.conflicts.map((conflict) => (
              <div
                key={conflict.doctorSubstituteId}
                className="mb-3"
              >
                <p className="font-medium text-sm">
                  <DoctorSubstituteName id={conflict.doctorSubstituteId} />
                </p>
                <ul className="list-disc pl-4 text-muted-foreground text-sm">
                  {conflict.conflictingPeriods.map((period) => (
                    <li key={`${period.from}-${period.to}`}>
                      {format(parseISO(period.from), 'dd/MM/yyyy')} →{' '}
                      {format(parseISO(period.to), 'dd/MM/yyyy')}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </CardContent>
        </Card>
      );
    }

    if (report.error.error === 'accounting_doctolib_missing_slots') {
      return (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="font-semibold text-md">
              {t(errorTranslationKeyOf(report.error.error))}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-3">
            <ul className="list-disc pl-4 text-muted-foreground text-sm">
              {report.error.data.missingDates.map((date) => (
                <li key={date}>{format(parseISO(date), 'dd/MM/yyyy')}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="font-semibold text-md">
            {t(
              errorTranslationKeyOf(
                (report.error as AccountingImportDoctolibCsvApiResponseError)
                  .error,
              ),
            )}
          </CardTitle>
        </CardHeader>
      </Card>
    );
  })();

  return (
    <Dialog
      open={true}
      onOpenChange={(open) => {
        if (!open) viewModel.api.close();
      }}
    >
      <DialogContent
        className="sm:max-w-2xl"
        showCloseButton={false}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>
            {t('import-accounting-doctor-attending-csv-report-modal.title')}
          </DialogTitle>
          <DialogDescription>
            {t(
              'import-accounting-doctor-attending-csv-report-modal.description',
            )}
          </DialogDescription>
        </DialogHeader>

        {contentByErrorType}

        <DialogFooter>
          <Button
            variant="secondary"
            onClick={viewModel.api.close}
          >
            {t('btn.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
